/**
 * Reddit Advertising Adapter (Story 19.10 OAuth + Story 19.11 Reports)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 *
 * Story 19.10 wired OAuth + Business/AdAccount discovery + token refresh +
 * connection validation. **Story 19.11 ships the reports pipeline** — replaces
 * the deferred `invalid_response` `fetchMetrics()` stub with the full hybrid
 * sync (6 parallel pipelines) + async-by-temporal-partitioning fallback,
 * the 159-entry metric catalogue (121 directive rows + 40 custom-event slot
 * expansions, minus the 2 template rows = 119 + 40), and the 21 dimensions.
 *
 * Reddit characteristics distinguishing this adapter from prior OAuth integrations:
 *   - HTTP Basic Authentication on token endpoint (Story 19.10)
 *   - User-Agent header MANDATORY on every API call (Reddit per-app rate-limit key)
 *   - `duration=permanent` MANDATORY on /authorize (Story 19.10)
 *   - **Refresh tokens DO NOT rotate** — refresh response omits the field
 *   - 60-minute access TTL (TD4 → inlineRefreshOn401: true REQUIRED)
 *   - Sales-gated API access — AC #13 surfaces `api_not_enabled` mapping
 *   - **DUAL CURRENCY DIVISORS** (Story 19.11) — micro÷1M and cents÷100; getting
 *     them mixed up corrupts conversion-value reporting by 4 orders of magnitude
 *   - **Attribution windows are DIMENSIONS in v3** (Aug 2025 deprecation of
 *     metric-suffix attribution-window split)
 *   - **Reach/Frequency available from June 2024 forward only**
 *   - **No `level=` enum, no `time_granularity` parameter** — both controlled
 *     by `breakdowns` array
 *   - **3-breakdown cap; 4 ONLY for COUNTRY+REGION combined breakdown**
 *   - **No async-with-poll primitive** — our "async fallback" is purely
 *     client-side temporal partitioning
 *
 * ADRs:
 *   - ADR-013 (Adapter Pattern): plain object implementing IntegrationAdapter
 *   - ADR-014 (API Versioning): `redditAds` lives in `API_VERSIONS`
 *   - ADR-016 (Aggressive Caching): `PLATFORM_CACHE_TTL["reddit-ads"] = 3600`
 *   - ADR-017 (Retry Logic): all HTTP calls flow through `safeFetchReddit`
 *     (Story 19.11 wires RFC 8941 RateLimit header parsing into retry decision)
 *   - ADR-020 (Token Lifecycle): Reddit refresh tokens permanent + non-rotating
 */

import type {
  AccountInfo,
  AdapterContext,
  AdapterErrorCode,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RefreshedTokens,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";
import { sanitizeErrorMessage } from "../safe-fetch";
import {
  fetchRedditAdAccountMetadata,
  REDDIT_FATAL_ERROR_CODES,
  runRedditAdsAsyncChunked,
  runRedditAdsParallelPipelines,
  type RedditAdAccountMetadata,
  type RedditAdsPipelineWarning,
} from "./analytics";
import {
  REDDIT_ADS_DIMENSIONS,
  REDDIT_ADS_METRICS,
  REDDIT_ADS_API_FIELDS,
  validateRedditAdsFieldList,
} from "./catalogue";
import {
  fetchRedditAdsMe,
  fetchRedditAdsBusinessesAndAdAccounts,
} from "./api";
import {
  RedditAdsApiCallError,
  redditAdsErrorTypeToAdapterCode,
} from "./errors";
import {
  RedditAdsTokenError,
  getRedditUserAgent,
  refreshRedditAdsToken,
} from "./oauth";
import { REDDIT_ADS_MAX_FETCHMETRICS_DAYS } from "./reports";
import type { AdapterRefreshContext } from "../oauth-families";

// ============================================================================
// Constants
// ============================================================================

/** API version is exported for parity with other adapters' module-level constants. */
export const REDDIT_ADS_API_VERSION = API_VERSIONS.redditAds;

/** Reddit alphanumeric+underscore namespace regex (prefix-agnostic per Round 2 Pre-mortem Failure B). */
const REDDIT_ACCOUNT_ID_REGEX = /^[A-Za-z0-9_-]+$/;

/** Delivery-data retention window (Reddit retains 24 months — AC #1). */
const REDDIT_RETENTION_MONTHS = 24;

/**
 * Default field list for the 6-pipeline sync fan-out. Picks a representative
 * cross-section of the catalogue: core delivery + spend + key conversions +
 * top-line video metrics + the 4 most-common conversion families
 * (Purchase / AddToCart / Lead / SignUp). Custom event slots, MMP/SKAN, and
 * niche dimensions are NOT in the default — widget-driven fetches that need
 * them go through `ctx.requestedColumns`.
 */
export const REDDIT_ADS_DEFAULT_FETCH_FIELDS: ReadonlyArray<string> = [
  // Core delivery
  "IMPRESSIONS",
  "REACH",
  "FREQUENCY",
  "CLICKS",
  "CTR",
  "ENGAGED_CLICK",
  // Spend
  "SPEND",
  "CPC",
  "ECPM",
  "ROAS",
  // Key conversion
  "KEY_CONVERSION_TOTAL_COUNT",
  "KEY_CONVERSION_RATE",
  "KEY_CONVERSION_ECPA",
  // Video summary
  "VIDEO_STARTED",
  "VIDEO_WATCHED_25_PERCENT",
  "VIDEO_WATCHED_50_PERCENT",
  "VIDEO_WATCHED_75_PERCENT",
  "VIDEO_WATCHED_100_PERCENT",
  "VIDEO_COMPLETION_RATE",
  // Top conversions — Purchase
  "CONVERSION_PURCHASE_CLICKS",
  "CONVERSION_PURCHASE_VIEWS",
  "CONVERSION_PURCHASE_TOTAL_COUNT",
  "CONVERSION_PURCHASE_TOTAL_VALUE",
  "CONVERSION_PURCHASE_AVG_VALUE",
  "CONVERSION_PURCHASE_ECPA",
  "CONVERSION_PURCHASE_CONVERSION_RATE",
  // Add to Cart
  "CONVERSION_ADD_TO_CART_CLICKS",
  "CONVERSION_ADD_TO_CART_TOTAL_COUNT",
  // Lead
  "CONVERSION_LEAD_CLICKS",
  "CONVERSION_LEAD_TOTAL_COUNT",
  "CONVERSION_LEAD_TOTAL_VALUE",
  // Sign Up
  "CONVERSION_SIGN_UP_CLICKS",
  "CONVERSION_SIGN_UP_TOTAL_COUNT",
];

// Verify default fields are in the catalogue allowlist at module load.
for (const f of REDDIT_ADS_DEFAULT_FETCH_FIELDS) {
  if (!REDDIT_ADS_API_FIELDS.has(f)) {
    throw new Error(
      `Reddit Ads DEFAULT_FETCH_FIELDS contains unknown field "${f}" — must be in REDDIT_ADS_API_FIELDS catalogue`
    );
  }
}

// ============================================================================
// Catalogue re-exports
// ============================================================================

export { REDDIT_ADS_METRICS, REDDIT_ADS_DIMENSIONS };

// ============================================================================
// Cache key builder (AC #11, #19)
// ============================================================================

/**
 * Build a canonical KV cache key for Reddit Ads metrics.
 *
 *   Format: `reddit-ads:{tenantId}:{adAccountId}:{startISO-date}:{endISO-date}`
 *
 * **Tenant-isolation enforcement** (AC #11, #19): rejects empty-string
 * `tenantId` synchronously to prevent silent cache-key collisions across
 * tenants. Mirrors `buildSnapchatAdsCacheKey` from Story 19.8.
 */
export function buildRedditAdsCacheKey(
  tenantId: string,
  adAccountId: string,
  dateRange: { start: Date; end: Date }
): string {
  if (typeof tenantId !== "string" || tenantId.length === 0) {
    throw new Error(
      "buildRedditAdsCacheKey: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (typeof adAccountId !== "string" || adAccountId.length === 0) {
    throw new Error("buildRedditAdsCacheKey: adAccountId is required");
  }
  const startIso = dateRange.start.toISOString().slice(0, 10);
  const endIso = dateRange.end.toISOString().slice(0, 10);
  return `reddit-ads:${tenantId}:${adAccountId}:${startIso}:${endIso}`;
}

// ============================================================================
// Internal helpers
// ============================================================================

function adapterErrorCodeFromError(err: unknown): AdapterErrorCode {
  if (err instanceof RedditAdsApiCallError) {
    return redditAdsErrorTypeToAdapterCode(err.type);
  }
  const msg = err instanceof Error ? err.message : String(err);
  if (/timeout|fetch failed|ENOTFOUND|ECONNRESET/i.test(msg)) {
    return "network_error";
  }
  return "invalid_response";
}

/**
 * Determines whether the request requires the async fallback path. Async is
 * triggered when:
 *   - breakdown count > 3 (and not the COUNTRY+REGION special case), OR
 *   - date range > 90 days (rejected at the validate step anyway, defensive), OR
 *   - `ctx.requestedLevel === "creative"` (synthetic — Reddit's gallery-card
 *     drill-down is conceptually separate from the default fan-out).
 */
function shouldUseAsyncFallback(ctx: AdapterContext, days: number): boolean {
  if (days > REDDIT_ADS_MAX_FETCHMETRICS_DAYS) return true;
  if (ctx.requestedLevel === "creative") return true;
  const requestedColumns = ctx.requestedColumns;
  // No requestedColumns array means use defaults — sync flow.
  if (requestedColumns === undefined) return false;
  // If caller requested unknown fields, surface as invalid_response upstream.
  // Async fallback fires when caller's column count is unreasonably large.
  // Reddit doesn't have a hard column cap, but extremely wide field lists
  // bloat per-row JSON — the sync flow handles them.
  return false;
}

/**
 * Compute days inclusive between two dates (UTC).
 */
function daysInclusive(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

/**
 * Compute the `dataFinalizedThrough` ISO timestamp:
 *   - `now() - 7d` when the queried fields include any conversion field
 *     (Reddit's view-attribution backfill window is ~7 days)
 *   - `now() - 1h` for delivery-only queries (Reddit emits delivery rows
 *     within seconds; the 1h floor covers the per-request finalisation window)
 */
function computeDataFinalizedThrough(fields: ReadonlyArray<string>): string {
  const hasConversion = fields.some((f) => f.startsWith("CONVERSION_") || f.startsWith("KEY_CONVERSION") || f.startsWith("APP_INSTALL_") || f === "ROAS");
  const ms = hasConversion ? 7 * 24 * 60 * 60 * 1000 : 60 * 60 * 1000;
  return new Date(Date.now() - ms).toISOString();
}

// ============================================================================
// Fixture-mode helper (Story 19.12 will populate fixtures)
// ============================================================================

async function loadRedditAdsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Reddit Ads demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve demo client slug",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Reddit Ads demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "reddit-ads",
    clientSlug
  );

  if (metrics === null) {
    return adapterErr({
      code: "account_not_found",
      message: `Reddit Ads demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end
  );
  return adapterOk(filtered);
}

// ============================================================================
// Adapter
// ============================================================================

export const redditAdsAdapter: IntegrationAdapter = {
  platform: "reddit-ads",

  rateLimits: {
    // Reddit's documented per-token quota varies by endpoint family; the
    // public Reddit API is 60 req/min/token for `ads-reporting`. RFC 8941
    // RateLimit headers from `parseRedditRateLimitHeaders` drive precise
    // backoff at retry time (Story 19.11 wires this via safeFetchReddit).
    requestsPerMinute: 60,
    retryAfterMs: 1000,
  },

  /**
   * Story 19.11 — Production fetchMetrics implementation.
   *
   * Flow:
   *   1. Fixture-mode short-circuit (Story 19.12 lands fixtures)
   *   2. Account ID validation (alphanumeric+underscore, prefix-agnostic)
   *   3. Date range validation (≤ 90 days, ≥ now - 24 months retention floor)
   *   4. Pre-fetch ad-account metadata (currency, time_zone_id, custom labels)
   *   5. Async-vs-sync routing per breakdown count + date range
   *   6. Run pipelines + multi-fatal race resolution
   *   7. Stamp metadata: currency, timezone, dataFinalizedThrough, customEventLabels
   *   8. Aggregate warnings; return AdapterMetrics
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // 1. Fixture mode short-circuit (AC #14)
    const fixtures =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixtures) {
      return loadRedditAdsFixture(ctx, dateRange);
    }

    // 2. Account ID validation (AC #1)
    const accountId = ctx.accountId ?? "";
    if (
      typeof accountId !== "string" ||
      accountId.length === 0 ||
      !REDDIT_ACCOUNT_ID_REGEX.test(accountId)
    ) {
      return adapterErr({
        code: "account_not_found",
        message:
          'Reddit Ads account ID must be a non-empty string (e.g. "a2_abc123")',
        retryable: false,
      });
    }

    // 3. Date range validation (AC #1)
    if (
      !(dateRange.start instanceof Date) ||
      !(dateRange.end instanceof Date) ||
      Number.isNaN(dateRange.start.getTime()) ||
      Number.isNaN(dateRange.end.getTime())
    ) {
      return adapterErr({
        code: "invalid_response",
        message: "Reddit Ads dateRange contains invalid Date values",
        retryable: false,
      });
    }
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_response",
        message: "Reddit Ads dateRange.start must be on or before dateRange.end",
        retryable: false,
      });
    }
    const days = daysInclusive(dateRange.start, dateRange.end);
    if (days > REDDIT_ADS_MAX_FETCHMETRICS_DAYS) {
      return adapterErr({
        code: "invalid_response",
        message: `Reddit Ads fetchMetrics is capped at ${REDDIT_ADS_MAX_FETCHMETRICS_DAYS} days; got ${days}`,
        retryable: false,
      });
    }
    // 24-month retention floor
    const retentionFloor = new Date();
    retentionFloor.setUTCMonth(retentionFloor.getUTCMonth() - REDDIT_RETENTION_MONTHS);
    if (dateRange.start.getTime() < retentionFloor.getTime()) {
      return adapterErr({
        code: "invalid_response",
        message: "Reddit Ads delivery data is retained for 24 months only",
        retryable: false,
      });
    }

    // Validate requestedColumns against the catalogue allowlist (closed-set guard)
    const fields: ReadonlyArray<string> =
      ctx.requestedColumns !== undefined
        ? ctx.requestedColumns
        : REDDIT_ADS_DEFAULT_FETCH_FIELDS;
    try {
      validateRedditAdsFieldList(fields);
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          err instanceof Error ? err.message : String(err)
        ),
        retryable: false,
      });
    }

    // 4. Pre-fetch ad-account metadata
    const userAgent = getRedditUserAgent(
      ctx.env as unknown as Record<string, string | undefined>
    );

    let accountMeta: RedditAdAccountMetadata;
    try {
      accountMeta = await fetchRedditAdAccountMetadata(
        credentials.accessToken,
        userAgent,
        accountId
      );
    } catch (err) {
      const code = adapterErrorCodeFromError(err);
      if (REDDIT_FATAL_ERROR_CODES.has(code)) {
        return adapterErr({
          code,
          message: sanitizeErrorMessage(
            `Reddit Ads ad-account metadata fetch failed: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable: code === "rate_limited" || code === "provider_unavailable",
        });
      }
      // Non-fatal — degrade to defaults (UTC + empty currency)
      accountMeta = {
        currencyCode: "",
        timeZoneId: "UTC",
        customEventLabels: null,
        name: "",
      };
    }

    const allWarnings: RedditAdsPipelineWarning[] = [];
    // Note: `fetchRedditAdAccountMetadata` defaults `timeZoneId` to "UTC" when
    // the discovery payload is missing the field. Reddit accounts typically
    // emit a real IANA zone — surfacing a warning here would be too noisy
    // since the UTC default is also legitimately configured for many test
    // accounts. The orchestrator-level `metadata.timeZone` lets the widget
    // layer differentiate UTC-by-default from UTC-explicit if needed.

    // 5. Routing — async vs sync
    const useAsync = shouldUseAsyncFallback(ctx, days);

    let records: ReadonlyArray<Record<string, unknown>> = [];
    let pipelineWarnings: ReadonlyArray<RedditAdsPipelineWarning> = [];
    let fatalCode: AdapterErrorCode | null = null;
    let fatalMessage: string | null = null;

    if (useAsync) {
      const r = await runRedditAdsAsyncChunked({
        accessToken: credentials.accessToken,
        userAgent,
        adAccountId: accountId,
        reportType: "campaigns",
        fields,
        breakdowns: ["CAMPAIGN_ID", "DATE"],
        startsAt: dateRange.start,
        endsAt: dateRange.end,
        timeZoneId: accountMeta.timeZoneId,
      });
      records = r.records;
      pipelineWarnings = r.warnings;
      fatalCode = r.fatalCode;
      fatalMessage = r.fatalMessage;
    } else {
      const r = await runRedditAdsParallelPipelines({
        accessToken: credentials.accessToken,
        userAgent,
        adAccountId: accountId,
        fields,
        startsAt: dateRange.start,
        endsAt: dateRange.end,
        timeZoneId: accountMeta.timeZoneId,
      });
      records = r.records;
      pipelineWarnings = r.warnings;
      fatalCode = r.fatalCode;
      fatalMessage = r.fatalMessage;
    }

    if (fatalCode !== null) {
      return adapterErr({
        code: fatalCode,
        message: sanitizeErrorMessage(
          `Reddit Ads fetchMetrics failed: ${fatalMessage ?? "unknown"}`
        ),
        retryable:
          fatalCode === "rate_limited" || fatalCode === "provider_unavailable",
      });
    }

    // 6. Build AdapterMetrics
    const warningsForResult = [...allWarnings, ...pipelineWarnings];
    const metricsResult: AdapterMetrics = {
      platform: "reddit-ads",
      accountId,
      dateRange,
      records: records as Record<string, unknown>[],
      fetchedAt: new Date(),
      recordCount: records.length,
      warnings: warningsForResult.map((w) => ({
        reportType: w.reportType,
        reason: w.reason,
      })),
      metadata: {
        currencyCode: accountMeta.currencyCode,
        timeZone: accountMeta.timeZoneId,
        dataFinalizedThrough: computeDataFinalizedThrough(fields),
        ...(accountMeta.customEventLabels !== null
          ? { customEventLabels: accountMeta.customEventLabels }
          : {}),
        ...(accountMeta.name.length > 0 ? { accountName: accountMeta.name } : {}),
      },
    };

    return adapterOk(metricsResult);
  },

  /**
   * Validates the Reddit connection by calling `/api/v3/me`. Unchanged from
   * Story 19.10.
   */
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const userAgent = getRedditUserAgent(
      ctx.env as unknown as Record<string, string | undefined>
    );
    try {
      await fetchRedditAdsMe(credentials.accessToken, userAgent);
      return adapterOk(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(
          `Reddit Ads connection validation failed: ${message}`
        ),
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  /**
   * Refreshes the Reddit Ads access token via the refresh_token grant.
   *
   * **Reddit refresh tokens DO NOT rotate.** Adapter carries forward the
   * existing `credentials.refreshToken` unchanged. Unchanged from Story 19.10.
   */
  async refreshToken(
    ctx: AdapterRefreshContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Reddit Ads",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.REDDIT_ADS_CLIENT_ID ?? "";
    const clientSecret = env.REDDIT_ADS_CLIENT_SECRET ?? "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing REDDIT_ADS_CLIENT_ID or REDDIT_ADS_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    const userAgent = getRedditUserAgent(env);

    try {
      const tokenResponse = await refreshRedditAdsToken(
        credentials.refreshToken,
        clientId,
        clientSecret,
        userAgent
      );

      const carriedRefreshToken =
        typeof tokenResponse.refresh_token === "string" &&
        tokenResponse.refresh_token.length > 0
          ? tokenResponse.refresh_token
          : credentials.refreshToken;

      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: carriedRefreshToken,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isInvalidGrant =
        err instanceof RedditAdsTokenError
          ? err.oauthError === "invalid_grant"
          : /invalid_grant/i.test(message);
      return adapterErr({
        code: isInvalidGrant ? "token_expired" : "token_invalid",
        message: sanitizeErrorMessage(
          `Reddit Ads token refresh failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  /**
   * Returns the flat list of ad accounts this OAuth grant can access.
   * Unchanged from Story 19.10.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const userAgent = getRedditUserAgent(
      ctx.env as unknown as Record<string, string | undefined>
    );
    try {
      const result = await fetchRedditAdsBusinessesAndAdAccounts(
        credentials.accessToken,
        userAgent
      );
      const accounts: AccountInfo[] = result.flatAdAccounts.map((acct) => ({
        id: acct.id,
        name: acct.name || `Account ${acct.id}`,
        platform: "reddit-ads",
        metadata: {
          currency: acct.currency,
          timezone: acct.timeZoneId,
          accountType: acct.type,
          adminApproval: acct.adminApproval,
          businessId: acct.businessId,
          businessName: acct.businessName,
        },
      }));
      return adapterOk(accounts);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return adapterErr({
        code: adapterErrorCodeFromError(err),
        message: sanitizeErrorMessage(
          `Reddit Ads account discovery failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return REDDIT_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return REDDIT_ADS_DIMENSIONS;
  },
};

